import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import * as adeDevicesApi from '@/services/adeDevicesApi';
import * as adeProfilesApi from '@/services/adeProfilesApi';
import type { AdeDeviceDTO } from '@/types/api';

export interface AdeDevice {
  id: string;
  serial_number: string;
  model: string | null;
  os: string | null;
  op_type: string | null;
  device_id: string | null;
  abm_connection_id: string;
  profile_uuid: string | null;
  profile_assign_time: string | null;
  device_assigned_date: string | null;
  device_assigned_by: string | null;
  sync_status: string | null;
  metadata: any | null;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  device?: {
    name: string;
    status: string;
  };
}

function mapToAdeDevice(dto: AdeDeviceDTO, adeAccountId: string): AdeDevice {
  return {
    id: dto.id,
    serial_number: dto.serialNumber,
    model: dto.model ?? null,
    os: dto.osVersion ?? null,
    op_type: null,
    device_id: dto.id,
    abm_connection_id: adeAccountId,
    profile_uuid: dto.profileId ?? null,
    profile_assign_time: null,
    device_assigned_date: null,
    device_assigned_by: null,
    sync_status: dto.profileStatus ?? null,
    metadata: null,
    last_synced_at: dto.lastSyncedAt ?? null,
    created_at: null,
    updated_at: null,
  };
}

export const useAdeDevices = (abmConnectionId?: string) => {
  const [devices, setDevices] = useState<AdeDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (abmConnectionId) {
      fetchDevices();
    } else {
      setLoading(false);
    }
  }, [abmConnectionId]);

  const fetchDevices = async () => {
    if (!abmConnectionId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await adeDevicesApi.listAdeDevices(abmConnectionId);
      setDevices(data.map((d) => mapToAdeDevice(d, abmConnectionId)));
    } catch (err) {
      console.error('Error fetching ADE devices:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch ADE devices'));
      toast({ title: 'Error', description: 'Failed to load ADE devices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const syncDevices = async () => {
    if (!abmConnectionId) return;
    try {
      setSyncing(true);
      const result = await adeDevicesApi.syncAllDevices(abmConnectionId);
      toast({
        title: 'Success',
        description: `Synced ${result.syncedCount || 0} devices from Apple Business Manager`,
      });
      await fetchDevices();
    } catch (err) {
      console.error('Error syncing ADE devices:', err);
      toast({ title: 'Error', description: 'Failed to sync devices from Apple Business Manager', variant: 'destructive' });
      throw err;
    } finally {
      setSyncing(false);
    }
  };

  const assignProfile = async (deviceIds: string[], profileUuid: string) => {
    if (!abmConnectionId) return;
    try {
      await adeProfilesApi.assignProfileToDevices(abmConnectionId, profileUuid, { devices: deviceIds });
      toast({ title: 'Success', description: `Enrollment profile assigned to ${deviceIds.length} device(s)` });
      await fetchDevices();
    } catch (err) {
      console.error('Error assigning profile to devices:', err);
      toast({ title: 'Error', description: 'Failed to assign enrollment profile to devices', variant: 'destructive' });
      throw err;
    }
  };

  const getDevicesByProfile = (profileUuid: string | null) =>
    devices.filter((device) => device.profile_uuid === profileUuid);

  const getUnassignedDevices = () =>
    devices.filter((device) => !device.profile_uuid);

  return {
    devices,
    loading,
    error,
    syncing,
    fetchDevices,
    syncDevices,
    assignProfile,
    getDevicesByProfile,
    getUnassignedDevices,
  };
};
